import { useLocation, useParams } from "react-router-dom"

export type BreadcrumbItem = {
  label: string
  href?: string
  isCurrentPage?: boolean
}

type BreadcrumbConfig = {
  [path: string]: (params?: Record<string, string | undefined>) => BreadcrumbItem[]
}

const breadcrumbConfig: BreadcrumbConfig = {
  "/": () => [
    { label: "Home", href: "/", isCurrentPage: true }
  ],
  
  "/regular-users": () => [
    { label: "Home", href: "/" },
    { label: "Regular Users", isCurrentPage: true }
  ],

  "/content-only-users": () => [
    { label: "Home", href: "/" },
    { label: "Content-Only Users", isCurrentPage: true }
  ],

  "/doctors": () => [
    { label: "Home", href: "/" },
    { label: "Doctors", isCurrentPage: true }
  ],
  
  "/orders": () => [
    { label: "Home", href: "/" },
    { label: "Orders", isCurrentPage: true }
  ],
  
  "/orders/:id": (params) => [
    { label: "Home", href: "/" },
    { label: "Orders", href: "/orders" },
    { label: `Order ${params?.id || 'Unknown'}`, isCurrentPage: true }
  ],
  
  "/users/:id": (params) => [
    { label: "Home", href: "/" },
    { label: `User ${params?.id || 'Unknown'}`, isCurrentPage: true }
  ],
  
  "/ai-qc": () => [
    { label: "Home", href: "/" },
    { label: "AI Reports", isCurrentPage: true }
  ],
  
  "/ai-qc/pending": () => [
    { label: "Home", href: "/" },
    { label: "AI Reports", href: "/ai-qc" },
    { label: "Pending Review", isCurrentPage: true }
  ],
  
  "/ai-qc/reviewed": () => [
    { label: "Home", href: "/" },
    { label: "AI Reports", href: "/ai-qc" },
    { label: "Reviewed", isCurrentPage: true }
  ],
  
  "/ai-qc/review/:reviewId": (params) => [
    { label: "Home", href: "/" },
    { label: "AI Reports", href: "/ai-qc" },
    { label: "Pending Review", href: "/ai-qc/pending" },
    { label: `Review ${params?.reviewId || 'Unknown'}`, isCurrentPage: true }
  ],
  
  "/tools": () => [
    { label: "Home", href: "/" },
    { label: "Tools", isCurrentPage: true }
  ],

  "/prompts": () => [
    { label: "Home", href: "/" },
    { label: "Prompts", isCurrentPage: true }
  ],

  "/event-logs": () => [
    { label: "Home", href: "/" },
    { label: "Event Logs", isCurrentPage: true }
  ],

  "/gcp-logs": () => [
    { label: "Home", href: "/" },
    { label: "GCP Logs", isCurrentPage: true }
  ],

  "/settings": () => [
    { label: "Home", href: "/" },
    { label: "Settings", isCurrentPage: true }
  ],
}

// Helper to match dynamic routes
function matchRoute(pathname: string, routePattern: string): boolean {
  const pathParts = pathname.split('/').filter(Boolean)
  const patternParts = routePattern.split('/').filter(Boolean)
  
  if (pathParts.length !== patternParts.length) {
    return false
  }
  
  return patternParts.every((pattern, index) => {
    return pattern.startsWith(':') || pattern === pathParts[index]
  })
}

// Get the matching route pattern for a pathname
function getMatchingRoute(pathname: string): string | null {
  // Try exact match first
  if (breadcrumbConfig[pathname]) {
    return pathname
  }
  
  // Try dynamic route matching
  for (const route of Object.keys(breadcrumbConfig)) {
    if (route.includes(':') && matchRoute(pathname, route)) {
      return route
    }
  }
  
  return null
}

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation()
  const params = useParams()
  
  const matchingRoute = getMatchingRoute(location.pathname)
  
  if (!matchingRoute || !breadcrumbConfig[matchingRoute]) {
    // Fallback breadcrumb
    return [{ label: "Home", href: "/", isCurrentPage: true }]
  }
  
  return breadcrumbConfig[matchingRoute]!(params)
}